import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import StatsCard from "common/Components/StatsCard";
import dataProvider from "data/dataProvider";
import React, { useEffect, useState } from "react";
import { Dropdown, DatePicker } from "@my-scoot/component-library-legacy";
import classnames from "classnames";
import { convertStringToDateObject } from "features/Common/modules/DateTime/utils/DateTime.utils";

import { date_range_select_options, STAT_CARDS } from "../data";
import { format, subDays, subYears } from "date-fns";
import {
  SHORT_MONTH_DATE,
  YEAR_MONTH_DAY_FORMAT,
} from "features/Common/modules/DateTime/DateTime.constants";
import { DAYS_RANGE } from "ui/pages/marketing/affiliates/affiliates.constants";
import ExlyImage from "features/Common/modules/ExlyImage/ExlyImage";
import { getS3AssetCDNUrl } from "features/Common/modules/File/modules/Upload/utils/FileUpload.utils";
import { api } from "data";
import { apiMethods } from "data/api.constants";
import { DRIP_SOURCE } from "../components/EmailListItem/EmailListItem.constants";
import { formatRevenue } from "features/Common/utils/common.utils";

const StatsLoadingState = ({ color }) => (
  <CircularProgress size={18} color={color} />
);

export const CustomEmailStats = () => {
  const isDesktop = useMediaQuery("(min-width: 767px)");
  const [performanceData, setperformanceData] = useState("");
  const [selectedRange, onRangeChange] = useState(
    date_range_select_options.last_7.value
  );
  const [dataRange, setDateRange] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const getDataRange = () => {
    const today = new Date();
    if (selectedRange === date_range_select_options.custom.value) {
      setStartDate("");
      setEndDate("");
      return "";
    }
    setEndDate(format(today, YEAR_MONTH_DAY_FORMAT));
    if (selectedRange === date_range_select_options.last_7.value) {
      const endRange = subDays(today, DAYS_RANGE.six);
      setStartDate(format(endRange, YEAR_MONTH_DAY_FORMAT));
      return `${format(endRange, SHORT_MONTH_DATE)} - ${format(
        today,
        SHORT_MONTH_DATE
      )}`;
    } else if (selectedRange === date_range_select_options.last_30.value) {
      const endRange = subDays(today, DAYS_RANGE.twentyNine);
      setStartDate(format(endRange, YEAR_MONTH_DAY_FORMAT));
      return `${format(endRange, SHORT_MONTH_DATE)} - ${format(
        today,
        SHORT_MONTH_DATE
      )}`;
    } else if (selectedRange === date_range_select_options.lifetime.value) {
      const endRange = subYears(today, DAYS_RANGE.one);
      setStartDate(format(endRange, YEAR_MONTH_DAY_FORMAT));
      return `${format(endRange, SHORT_MONTH_DATE)} - ${format(
        today,
        SHORT_MONTH_DATE
      )}`;
    }
  };

  useEffect(() => {
    setDateRange(getDataRange);
  }, [selectedRange]);

  useEffect(() => {
    if (!startDate || !endDate) return;
    (async () => {
      setperformanceData("");
      const statisticsData = await dataProvider.custom_request(
        api.get_email_stats,
        apiMethods.GET,
        {
          start_date: startDate,
          end_date: endDate,
          drip_source: DRIP_SOURCE.SALES_AND_MARKETING,
        }
      );
      if (statisticsData.status === 200) {
        setperformanceData(statisticsData.data);
      }
    })();
  }, [startDate, endDate]);

  return (
    <Box className="tw-mt-[20px] tw-mb-[10px] tw-mx-4">
      <div
        className={classnames(
          "tw-mb-[18px] tw-flex",
          selectedRange === date_range_select_options.custom.value
            ? "tw-flex-col md:tw-flex-row tw-gap-3 md:tw-gap-0 md:tw-items-center"
            : "tw-flex-row-reverse md:tw-flex-row tw-justify-between md:tw-justify-normal tw-items-center"
        )}
      >
        <Dropdown
          options={Object.values(date_range_select_options)}
          value={selectedRange}
          handleChange={onRangeChange}
          placeholder="Select date range"
          className={`mr-md-4 !tw-h-[40px] !tw-w-[153px] tw-border-[1.5px] tw-border-solid tw-border-[#cfcdc9] tw-rounded-[5px]`}
        />
        {selectedRange !== date_range_select_options.custom.value ? (
          <p className="!tw-m-0 tw-text-[16px] tw-font-medium tw-tracking-[0.64px] tw-flex tw-items-center">
            {dataRange}
          </p>
        ) : (
          <div className="tw-flex tw-w-full md:tw-w-auto tw-items-center">
            <DatePicker
              size={!isDesktop ? "thin" : "small"}
              popperClassName="!tw-z-[11]"
              placeholder="From date"
              value={
                startDate
                  ? convertStringToDateObject({ dateString: startDate })
                  : null
              }
              onChange={(date) =>
                setStartDate(format(date, YEAR_MONTH_DAY_FORMAT))
              }
              wrapperClassName="tw-w-1/2 md:tw-w-[140px] tw-mr-2 !tw-h-[40px] tw-bg-white"
            />
            <DatePicker
              popperPlacement={isDesktop ? "bottom-end" : ""}
              size={!isDesktop ? "thin" : "small"}
              wrapperClassName={classnames(
                "tw-w-1/2 md:tw-w-[140px] md:tw-ml-2 !tw-h-[40px] tw-bg-white",
                {
                  "!tw-pointer-events-none": !startDate,
                }
              )}
              popperClassName="!tw-z-[11]"
              minDate={
                startDate
                  ? convertStringToDateObject({ dateString: startDate })
                  : null
              }
              disabled={!startDate}
              placeholder="To date"
              value={
                endDate
                  ? convertStringToDateObject({ dateString: endDate })
                  : null
              }
              onChange={(date) =>
                setEndDate(format(date, YEAR_MONTH_DAY_FORMAT))
              }
            />
          </div>
        )}
      </div>
      <div className="tw-flex tw-w-full tw-gap-[15px]">
        <StatsCard
          className="tw-mb-4 md:tw-mb-0 !tw-bg-[#E9FBFF]"
          color="primary"
          linkText=""
          title={STAT_CARDS.OPEN_RATE.title}
          tooltip="Number of people who showed interest in your webpage or offering pages."
          statsNumber={
            performanceData ? (
              performanceData?.avg_open_rate
            ) : (
              <StatsLoadingState color="primary" />
            )
          }
          Icon={() => (
            <ExlyImage
              src={getS3AssetCDNUrl("assets/vectors/dashboard/percent.png")}
              width={28}
              height={28}
              className="!tw-mr-[16px]"
            />
          )}
          classes={{
            textColorClass: "!tw-text-[#086F86]",
          }}
        />
        <StatsCard
          className="tw-mb-4 md:tw-mb-0"
          color="primary"
          linkText=""
          title={STAT_CARDS.BOOKINGS.title}
          tooltip="Number of bookings received via all tracking links"
          statsNumber={
            performanceData ? (
              performanceData?.total_bookings
            ) : (
              <StatsLoadingState color="primary" />
            )
          }
          Icon={() => (
            <ExlyImage
              src={getS3AssetCDNUrl("assets/vectors/dashboard/booking.png")}
              width={28}
              height={28}
              className="!tw-mr-[16px]"
            />
          )}
        />
        <StatsCard
          className="tw-mb-4 md:tw-mb-0"
          color="fine_pine"
          linkText=""
          title={STAT_CARDS.REVENUE.title}
          tooltip="Total revenue received via all tracking links"
          statsNumber={
            performanceData ? (
              formatRevenue(performanceData?.total_revenue)
            ) : (
              <StatsLoadingState color="fine_pine" />
            )
          }
          Icon={() => (
            <ExlyImage
              src={getS3AssetCDNUrl("assets/vectors/dashboard/revenue.png")}
              width={28}
              height={28}
              className="!tw-mr-[16px]"
            />
          )}
        />
      </div>
    </Box>
  );
};
